import { useQuery, useQueryClient } from '@tanstack/react-query';

export function createGlobalState(
  queryKey,
  queryFunction,
  defaultTime = 60_000,
  options = {},
) {
  const { keepPreviousData = true } = options;
  return function (id = null, condition = true, time = defaultTime) {
    const queryClient = useQueryClient();
    const fullQueryKey = id ? [queryKey, id] : [queryKey];

    const { data, isLoading, isFetching, isPreviousData, isError, error } =
      useQuery({
        queryKey: fullQueryKey,
        queryFn: async () => {
          const result = await (id ? queryFunction(id) : queryFunction());
          return result;
        },
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        keepPreviousData,
        enabled: condition,
        staleTime: time,
        cacheTime: time,
      });

    // isLoadingdata: true only on initial load (no cached data)
    // isFetching: true whenever a request is in flight (initial or refetch)
    // Use isLoadingdata for blocking UI, isFetching for refresh indicators
    const isLoadingdata = isLoading;

    function setData(data) {
      queryClient.setQueryData(fullQueryKey, data);
    }

    // One request per cached entry, inactive ones included. Invalidating and
    // then calling refetchQueries sent two: the second cancelled the first
    // client-side only, so the backend still served both.
    function resetData() {
      return queryClient.invalidateQueries({
        queryKey: fullQueryKey,
        refetchType: 'all',
      });
    }

    return {
      data,
      isLoadingdata,
      isFetching,
      isPreviousData,
      isError,
      error,
      setData,
      resetData,
      // The prefix resetData refreshes.
      queryKey: fullQueryKey,
      cancelData: () => queryClient.cancelQueries({ queryKey: fullQueryKey }, { revert: false }),
    };
  };
}
